import { MouseEvent, MouseEventHandler, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import styled, { useTheme } from 'styled-components'
import { Text } from '@/components/common'
import { ButtonScrollTop } from '@/components/common/buttons'
import FlatIcon from '@/components/common/icons/FlatIcon'
import { IconNameEnum } from '@/constants/icon-name.enum'
import { SCREEN_SCALE, Z_INDEXES } from '@/constants/legacy/constLayout'
import { EventContainer } from '@/containers/contexts/regional/event.container'
import useEventDetailEventTracker from '@/containers/event-tracker/event-detail.event-tracker'
import { hasSignedIn } from '@/containers/hooks/useUserInfo'
import { UserContainer } from '@/containers/users/UserContainer'
import { useAdCouponDownloadButtonQuery } from '@/graphql/generated/hooks'
import CouponImage from '@/images/coupon.svg'
import { doSetToggleModalLogin } from '@/stores/reduxUI'
import { isInIos, isIOSWeb, throttle } from '@/utils/utilCommon'

interface Props {
  openModal: () => void
}

const title = {
  canIssue: '쿠폰 다운받기',
  canNotIssue: '쿠폰을 모두 받았어요!',
}

const EventDetailCouponDownloadButton = ({ openModal }: Props) => {
  const { me } = UserContainer.useContainer()
  const dispatch = useDispatch()
  const { fontWeight, color, iconSize } = useTheme()
  const [isVisible, setIsVisible] = useState(false)
  const { ad } = EventContainer.useContainer()
  const { data, loading } = useAdCouponDownloadButtonQuery({
    variables: {
      slug: ad.slug ?? '',
    },
    onCompleted: (data) => {
      if (data?.adBySlug?.couponGroups?.edges?.length) {
        setIsVisible(true)
      }
    },
  })

  const goToLogin = () => {
    dispatch(doSetToggleModalLogin(true))
  }

  const { trackerClickEventsaleCouponButton } = useEventDetailEventTracker()
  const handleClick = () => {
    /** 비로그인 상태의 버튼클릭도 트래킹이 필요하므로 항상 호출 */
    trackerClickEventsaleCouponButton({
      adId: ad.id,
      adName: ad.name,
      adSlug: ad.slug || '',
      buttonText: title.canIssue,
      isLogin: !!me,
    })

    if (!hasSignedIn()) {
      return goToLogin()
    }

    openModal()
  }

  const handleButtonContainer: MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation()
  }

  const isIOS = isInIos() || isIOSWeb()

  /**
   * 스크롤 방향에 따라 ButtonScrollTop 보여주기
   */
  const [showTopButton, setShowTopButton] = useState<boolean>(false)
  const lastScrollRef = useRef<number>(0)
  useEffect(() => {
    const wait = 300
    const move = 100
    const onScroll = throttle(() => {
      const { pageYOffset } = window
      setShowTopButton(pageYOffset > move && lastScrollRef.current > pageYOffset)
      lastScrollRef.current = pageYOffset
    }, wait)

    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const handleCloseClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    setIsVisible(false)
  }

  const notLogin = !me
  const shouldDisplayDownloadText = !data?.adBySlug?.isAllCouponsDownloaded

  return (
    <StyledButtonContainer isVisible={isVisible} isIOS={isIOS} onClick={handleButtonContainer}>
      <StyledButtonScrollTop>
        <ButtonScrollTop showTopButton={showTopButton} />
      </StyledButtonScrollTop>
      <StyledCouponButton onClick={handleClick}>
        {shouldDisplayDownloadText ? (
          <>
            <CouponImage />
            <Text.Body1 fontWeight={fontWeight.bold} fontColor={color.grayWhite}>
              {title.canIssue}
            </Text.Body1>
          </>
        ) : (
          <div className="canNotIssue">
            <Text.Body1 fontColor={color.grayWhite} fontWeight={fontWeight.bold}>
              {title.canNotIssue}
            </Text.Body1>
            <div className="icClose" onClick={handleCloseClick}>
              <FlatIcon type={IconNameEnum.IcClose} color={color.grayWhite} size={iconSize.size20} />
            </div>
          </div>
        )}
      </StyledCouponButton>
    </StyledButtonContainer>
  )
}

const StyledButtonContainer = styled.div<{ isIOS: boolean; isVisible: boolean }>`
  z-index: ${Z_INDEXES.SCROLL_TOP_BUTTON};
  position: fixed;
  bottom: calc(2.6rem + ${(props) => (props.isIOS ? 'var(--safe-bottom-extra)' : '0px')});
  background-color: transparent;
  pointer-events: none;
  display: ${(props) => (props.isVisible ? 'flex' : 'none')};
  flex-direction: column;
  row-gap: 1.6rem;
  align-items: flex-end;
`

const StyledButtonScrollTop = styled.div`
  div:first-child {
    margin-right: 0;
  }
`

const StyledCouponButton = styled.button`
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: 0.6rem;
  padding: 1.3rem 2rem;
  border-radius: 2.4rem;
  border: none;
  cursor: pointer;
  width: 34.3rem;
  margin-left: 1.6rem;
  background-color: ${({ theme }) => theme.color.grayBlack};
  user-select: none;

  &:active {
    transform: scale(0.96);
  }

  .canNotIssue {
    display: flex;
    justify-content: space-between;
    align-items: center;
    column-gap: 1.6rem;
    width: 100%;

    > *:first-child {
      flex-grow: 1;
      padding-left: 3.6rem;
    }

    .icClose {
      position: relative;

      &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: calc(100% + 0.8rem);
        height: calc(100% + 0.8rem);
      }
    }
  }

  @media (min-width: ${SCREEN_SCALE.MAX_SCALE_DEVICE_WIDTH}px) {
    width: calc(100vw - 32px);
  }

  @media (min-width: ${SCREEN_SCALE.MAX_DEVICE_WIDTH}px) {
    width: calc(576px - 32px);
  }
`

export default EventDetailCouponDownloadButton
